import { supabase, adminSupabase } from './supabaseService';
import type { User } from '../types/supabase';
import { hashWalletPassword, generateResetToken, verifyWalletPassword } from '../utils/walletCryptoUtils';

// Optimized cache mechanism with longer TTL and batch operations
const profileCache = new Map<string, { profile: any, timestamp: number }>();
const CACHE_TTL = 300000; // 5 minute cache time (increased from 1 minute)
const BATCH_CACHE_TTL = 180000; // 3 minutes for batch operations

// Performance configuration
const QUERY_TIMEOUT = 10000; // 10 seconds
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 200; // 200ms

/**
 * Retry operation with exponential backoff
 */
async function retryOperation<T>(
    operation: () => Promise<T>,
    maxRetries: number = MAX_RETRIES,
    initialDelay: number = INITIAL_RETRY_DELAY
): Promise<T> {
    let retries = 0;

    while (true) {
        try {
            return await operation();
        } catch (error: any) {
            retries++;

            if (retries >= maxRetries) {
                throw error;
            }

            // Skip retry for certain errors
            if (error?.code === 'PGRST116') { // Row not found
                throw error;
            }

            const delay = initialDelay * Math.pow(2, retries - 1);
            console.warn(`User profile operation failed, retrying in ${delay}ms (attempt ${retries}/${maxRetries}):`, error.message);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
}

/**
 * Add timeout to queries
 */
function createAbortTimeout(timeoutMs: number = QUERY_TIMEOUT): { abortController: AbortController; timeoutId: NodeJS.Timeout } {
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), timeoutMs);
    return { abortController, timeoutId };
}

// App salt for password hashing
const APP_SALT = 'EKKO_WALLET_PASSWORD_SALT';

export interface UserProfile {
    id?: string; // Add id field for trade logging
    display_name: string | null;
    email: string | null;
    twitter_handle: string | null;
    telegram_handle: string | null;
    discord_handle: string | null;
    avatar_url: string | null;
    bio: string | null;
    last_display_name_change: string | null;
    active_trade_wallet: string | null;
}

// Utility to notify wallet connection component of display name change
export const notifyDisplayNameChange = (displayName: string | null) => {
    const event = new CustomEvent('ekko:display_name_changed', {
        detail: { displayName }
    });
    window.dispatchEvent(event);
};

// Utility function to notify components about active wallet changes
export const notifyActiveWalletChanged = (previousWallet: string | null, currentWallet: string | null) => {
    const event = new CustomEvent('ekko:active_wallet_changed', {
        detail: {
            previousWallet,
            currentWallet,
            timestamp: Date.now()
        }
    });
    window.dispatchEvent(event);
};

/**
 * Optimized batch profile fetcher for multiple wallet addresses
 */
export const getUserProfiles = async (walletAddresses: string[]): Promise<Map<string, UserProfile | null>> => {
    const result = new Map<string, UserProfile | null>();
    const uncachedAddresses: string[] = [];

    // Check cache first
    const now = Date.now();
    for (const address of walletAddresses) {
        const cachedProfile = profileCache.get(address);
        if (cachedProfile && now - cachedProfile.timestamp < BATCH_CACHE_TTL) {
            result.set(address, cachedProfile.profile);
        } else {
            uncachedAddresses.push(address);
        }
    }

    // Fetch uncached profiles in batch
    if (uncachedAddresses.length > 0) {
        try {
            await retryOperation(async () => {
                const { data, error } = await adminSupabase
                    .from('users')
                    .select('id, wallet_address, display_name, email, twitter_handle, telegram_handle, discord_handle, avatar_url, bio, last_display_name_change, active_trade_wallet')
                    .in('wallet_address', uncachedAddresses);

                if (error && error.code !== 'PGRST116') {
                    console.error("Error batch fetching user profiles:", error);
                }

                // Process results
                const foundProfiles = new Map<string, UserProfile>();
                if (data && data.length > 0) {
                    for (const profile of data) {
                        foundProfiles.set(profile.wallet_address, profile);
                        // Cache the profile
                        profileCache.set(profile.wallet_address, {
                            profile: profile,
                            timestamp: now
                        });
                    }
                }

                // Set results for all requested addresses
                for (const address of uncachedAddresses) {
                    const profile = foundProfiles.get(address) || null;
                    result.set(address, profile);

                    // Cache null results too to avoid repeated queries
                    if (!profile) {
                        profileCache.set(address, {
                            profile: null,
                            timestamp: now
                        });
                    }
                }
            });
        } catch (error) {
            console.error("Exception batch fetching user profiles:", error);
            // Set null for failed addresses
            for (const address of uncachedAddresses) {
                result.set(address, null);
            }
        }
    }

    return result;
};

/**
 * Gets a user profile from Supabase by wallet address (optimized)
 */
export const getUserProfile = async (walletAddress: string): Promise<UserProfile | null> => {
    try {
        // Check cache first
        const cachedProfile = profileCache.get(walletAddress);
        if (cachedProfile && Date.now() - cachedProfile.timestamp < CACHE_TTL) {
            return cachedProfile.profile;
        }

        return await retryOperation(async () => {
            const { data, error } = await adminSupabase
                .from('users')
                .select('id, display_name, email, twitter_handle, telegram_handle, discord_handle, avatar_url, bio, last_display_name_change, active_trade_wallet')
                .eq('wallet_address', walletAddress)
                .single();

            if (error) {
                // If error is not found, we'll return null without logging an error
                if (error.code !== 'PGRST116') {
                    console.error("Error fetching user profile:", error);
                }
                return null;
            }

            if (!data) {
                return null;
            }

            // Cache the profile with extended TTL
            profileCache.set(walletAddress, {
                profile: data,
                timestamp: Date.now()
            });

            return data;
        });
    } catch (error) {
        console.error("Exception fetching user profile:", error);
        return null;
    }
};

/**
 * Check if a user can change their display name (limited to once per week)
 */
export const canChangeDisplayName = async (walletAddress: string): Promise<{
    canChange: boolean;
    timeLeft?: number; // Time left in milliseconds
    nextChangeDate?: Date;
}> => {
    try {
        const { data, error } = await adminSupabase
            .from('users')
            .select('last_display_name_change')
            .eq('wallet_address', walletAddress)
            .single();

        if (error || !data) {
            // If no data or error, user likely doesn't exist, so they can change
            return { canChange: true };
        }

        // If user has never changed their display name
        if (!data.last_display_name_change) {
            return { canChange: true };
        }

        const lastChange = new Date(data.last_display_name_change);
        const now = new Date();
        const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

        const nextChangeDate = new Date(lastChange.getTime() + oneWeek);
        const timeLeft = nextChangeDate.getTime() - now.getTime();

        return {
            canChange: timeLeft <= 0,
            timeLeft: timeLeft > 0 ? timeLeft : 0,
            nextChangeDate: timeLeft > 0 ? nextChangeDate : undefined
        };
    } catch (error) {
        console.error("Error checking if user can change display name:", error);
        // Default to allowing change in case of error
        return { canChange: true };
    }
};

/**
 * Creates or updates a user profile in Supabase
 */
export const saveUserProfile = async (
    walletAddress: string,
    profile: UserProfile
): Promise<{
    success: boolean;
    canChangeDisplayName?: boolean;
    timeLeft?: number;
    nextChangeDate?: Date;
}> => {

    try {
        // First check if user is trying to change display name
        let displayNameChanged = false;

        // Fetch current profile to check if display name is changing
        const currentProfile = await getUserProfile(walletAddress);

        // Check if display name is being changed - only count non-empty strings
        if (currentProfile &&
            profile.display_name !== undefined &&
            profile.display_name !== null &&
            profile.display_name !== currentProfile.display_name &&
            profile.display_name.trim() !== '') { // Only consider non-empty strings as real name changes
            displayNameChanged = true;

            // Check if user can change their display name
            const { canChange, timeLeft, nextChangeDate } = await canChangeDisplayName(walletAddress);

            if (!canChange) {
                console.warn("User cannot change display name yet. Time left:", timeLeft);
                return {
                    success: false,
                    canChangeDisplayName: false,
                    timeLeft,
                    nextChangeDate
                };
            }
        }

        // Check if the user exists - using adminSupabase to bypass RLS
        const { data: existingUser } = await adminSupabase
            .from('users')
            .select('id, display_name')
            .eq('wallet_address', walletAddress)
            .single();

        // Prepare update data
        const updateData: any = {
            display_name: profile.display_name,
            email: profile.email,
            twitter_handle: profile.twitter_handle,
            telegram_handle: profile.telegram_handle,
            discord_handle: profile.discord_handle,
            avatar_url: profile.avatar_url,
            bio: profile.bio,
            updated_at: new Date().toISOString()
        };

        // If display name is changed, update the last_display_name_change timestamp
        if (displayNameChanged) {
            updateData.last_display_name_change = new Date().toISOString();
        }

        let success = false;

        if (existingUser) {
            // Update existing user - using adminSupabase to bypass RLS
            const { error } = await adminSupabase
                .from('users')
                .update(updateData)
                .eq('wallet_address', walletAddress);

            if (error) {
                console.error("Error updating user profile:", error);
                return { success: false };
            }

            success = true;

            // If display name was changed, prepare response with next change date
            if (displayNameChanged) {
                const now = new Date();
                const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
                const nextChangeDate = new Date(now.getTime() + oneWeek);

                // Notify components about display name change
                notifyDisplayNameChange(profile.display_name || null);

                // Clear the profile cache for this user
                profileCache.delete(walletAddress);

                return {
                    success: true,
                    timeLeft: oneWeek,
                    nextChangeDate
                };
            }
        } else {
            // Create new user - using adminSupabase to bypass RLS
            const newUserData: any = {
                wallet_address: walletAddress,
                wallet_provider: 'unknown', // This will be updated by the wallet connection
                display_name: profile.display_name,
                email: profile.email,
                twitter_handle: profile.twitter_handle,
                telegram_handle: profile.telegram_handle,
                discord_handle: profile.discord_handle,
                avatar_url: profile.avatar_url,
                bio: profile.bio,
                last_login: new Date().toISOString(),
                login_count: 1,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            // If this is the first time setting a display name, track it
            // Only track actual non-empty display names
            if (profile.display_name && profile.display_name !== null && profile.display_name.trim() !== '') {
                newUserData.last_display_name_change = new Date().toISOString();
            }

            const { error } = await adminSupabase
                .from('users')
                .insert([newUserData]);

            if (error) {
                console.error("Error creating user profile:", error);
                return { success: false };
            }

            success = true;

            // If display name was set, notify components
            if (profile.display_name) {
                notifyDisplayNameChange(profile.display_name);
            }
        }

        // Clear the profile cache for this user if the operation was successful
        if (success) {
            profileCache.delete(walletAddress);
        }

        return { success };
    } catch (error) {
        console.error("Exception saving user profile:", error);
        return { success: false };
    }
};

/**
 * Optimized wallet connection update with better performance
 */
export const updateWalletConnection = async (
    walletAddress: string,
    provider: string
): Promise<void> => {
    try {
        await retryOperation(async () => {
            // First check if user exists to avoid unnecessary inserts
            const { data: existingUser, error: checkError } = await adminSupabase
                .from('users')
                .select('id, login_count')
                .eq('wallet_address', walletAddress)
                .single();

            if (existingUser) {
                // Update existing user
                const { error: updateError } = await adminSupabase
                    .from('users')
                    .update({
                        wallet_provider: provider,
                        last_login: new Date().toISOString(),
                        login_count: (existingUser.login_count || 0) + 1,
                        updated_at: new Date().toISOString()
                    })
                    .eq('wallet_address', walletAddress);

                if (updateError) {
                    throw updateError;
                }
            } else {
                // Create new user with minimal info
                const { error: insertError } = await adminSupabase
                    .from('users')
                    .insert([{
                        wallet_address: walletAddress,
                        wallet_provider: provider,
                        last_login: new Date().toISOString(),
                        login_count: 1,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    }]);

                if (insertError) {
                    throw insertError;
                }
            }

            // Clear the profile cache for this user after updating
            profileCache.delete(walletAddress);
        });
    } catch (error) {
        console.error("Error updating wallet connection:", error);
        throw error; // Re-throw to let caller handle
    }
};

/**
 * Updates notification preferences
 */
export const updateNotificationPreferences = async (
    walletAddress: string,
    preferences: Record<string, boolean>
): Promise<boolean> => {
    try {
        const { data: existingUser } = await adminSupabase
            .from('users')
            .select('user_preferences')
            .eq('wallet_address', walletAddress)
            .single();

        let updatedPreferences = preferences;

        // Merge with existing preferences if they exist
        if (existingUser && existingUser.user_preferences) {
            updatedPreferences = {
                ...existingUser.user_preferences,
                ...preferences
            };
        }

        const { error } = await adminSupabase
            .from('users')
            .update({
                user_preferences: updatedPreferences,
                updated_at: new Date().toISOString()
            })
            .eq('wallet_address', walletAddress);

        if (error) {
            console.error("Error updating notification preferences:", error);
            return false;
        }

        return true;
    } catch (error) {
        console.error("Exception updating notification preferences:", error);
        return false;
    }
};

/**
 * Gets notification preferences from the user profile
 */
export const getNotificationPreferences = async (
    walletAddress: string
): Promise<Record<string, boolean> | null> => {
    try {
        const { data, error } = await adminSupabase
            .from('users')
            .select('user_preferences')
            .eq('wallet_address', walletAddress)
            .single();

        if (error || !data) {
            return null;
        }

        return data.user_preferences as Record<string, boolean> || {};
    } catch (error) {
        console.error("Exception getting notification preferences:", error);
        return null;
    }
};

/**
 * Active Positions Quick Sell Preferences Interface
 */
export interface ActivePositionsQuickSellPreferences {
    sellPercentage: number;        // Percentage of tokens to sell (1-100)
    confirmBeforeSell: boolean;    // Whether to show confirmation dialog
    slippagePercent: number;       // Slippage tolerance for quick sells
    enabled: boolean;              // Whether quick sell is enabled
}

/**
 * Gets Active Positions quick sell preferences
 */
export const getActivePositionsQuickSellPreferences = async (
    walletAddress: string
): Promise<ActivePositionsQuickSellPreferences> => {
    try {
        const { data, error } = await adminSupabase
            .from('users')
            .select('user_preferences')
            .eq('wallet_address', walletAddress)
            .single();

        const defaultPreferences: ActivePositionsQuickSellPreferences = {
            sellPercentage: 25,           // Default to 25% of holdings
            confirmBeforeSell: true,      // Always confirm by default
            slippagePercent: 5,          // 5% slippage tolerance
            enabled: true                // Quick sell enabled by default
        };

        if (error || !data || !data.user_preferences) {
            return defaultPreferences;
        }

        const userPrefs = data.user_preferences as Record<string, any>;
        const quickSellPrefs = userPrefs.activePositionsQuickSell;

        if (!quickSellPrefs || typeof quickSellPrefs !== 'object') {
            return defaultPreferences;
        }

        // Merge with defaults to ensure all fields are present
        return {
            sellPercentage: Math.max(1, Math.min(100, quickSellPrefs.sellPercentage || defaultPreferences.sellPercentage)),
            confirmBeforeSell: quickSellPrefs.confirmBeforeSell !== undefined ? quickSellPrefs.confirmBeforeSell : defaultPreferences.confirmBeforeSell,
            slippagePercent: Math.max(0.1, Math.min(50, quickSellPrefs.slippagePercent || defaultPreferences.slippagePercent)),
            enabled: quickSellPrefs.enabled !== undefined ? quickSellPrefs.enabled : defaultPreferences.enabled
        };
    } catch (error) {
        console.error("Exception getting Active Positions quick sell preferences:", error);
        // Return safe defaults on error
        return {
            sellPercentage: 25,
            confirmBeforeSell: true,
            slippagePercent: 5,
            enabled: true
        };
    }
};

/**
 * Updates Active Positions quick sell preferences
 */
export const updateActivePositionsQuickSellPreferences = async (
    walletAddress: string,
    preferences: Partial<ActivePositionsQuickSellPreferences>
): Promise<boolean> => {
    try {
        // Get current preferences
        const currentPrefs = await getActivePositionsQuickSellPreferences(walletAddress);

        // Merge new preferences with current ones
        const updatedQuickSellPrefs: ActivePositionsQuickSellPreferences = {
            ...currentPrefs,
            ...preferences
        };

        // Validate ranges
        updatedQuickSellPrefs.sellPercentage = Math.max(1, Math.min(100, updatedQuickSellPrefs.sellPercentage));
        updatedQuickSellPrefs.slippagePercent = Math.max(0.1, Math.min(50, updatedQuickSellPrefs.slippagePercent));

        // Get existing user preferences
        const { data: existingUser } = await adminSupabase
            .from('users')
            .select('user_preferences')
            .eq('wallet_address', walletAddress)
            .single();

        let allPreferences = {};
        if (existingUser && existingUser.user_preferences) {
            allPreferences = existingUser.user_preferences as Record<string, any>;
        }

        // Update just the Active Positions quick sell preferences
        const updatedAllPreferences = {
            ...allPreferences,
            activePositionsQuickSell: updatedQuickSellPrefs
        };

        // Save to database
        const { error } = await adminSupabase
            .from('users')
            .update({
                user_preferences: updatedAllPreferences,
                updated_at: new Date().toISOString()
            })
            .eq('wallet_address', walletAddress);

        if (error) {
            console.error("Error updating Active Positions quick sell preferences:", error);
            return false;
        }

        // Clear cache
        profileCache.delete(walletAddress);

        console.log(`✅ Updated Active Positions quick sell preferences for ${walletAddress.slice(0, 8)}...`);
        return true;
    } catch (error) {
        console.error("Exception updating Active Positions quick sell preferences:", error);
        return false;
    }
};

// Set wallet key protection password
export async function setWalletKeyPassword(walletAddress: string, password: string): Promise<boolean> {
    try {
        // Hash the password for secure storage
        const passwordHash = hashWalletPassword(password, APP_SALT);

        // Update the user table with the hashed password
        const { error } = await adminSupabase
            .from('users')
            .update({ wallet_key_password_hash: passwordHash })
            .eq('wallet_address', walletAddress);

        if (error) {
            console.error('Error setting wallet key password:', error);
            return false;
        }

        return true;
    } catch (error) {
        console.error('Error setting wallet key password:', error);
        return false;
    }
}

// Check if wallet key password exists
export async function hasWalletKeyPassword(walletAddress: string): Promise<boolean> {
    try {
        const { data, error } = await adminSupabase
            .from('users')
            .select('wallet_key_password_hash')
            .eq('wallet_address', walletAddress)
            .single();

        if (error || !data) {
            return false;
        }

        return !!data.wallet_key_password_hash;
    } catch (error) {
        console.error('Error checking wallet key password:', error);
        return false;
    }
}

// Verify wallet key password
export async function verifyWalletKeyPassword(walletAddress: string, password: string): Promise<boolean> {
    try {
        const { data, error } = await adminSupabase
            .from('users')
            .select('wallet_key_password_hash')
            .eq('wallet_address', walletAddress)
            .single();

        if (error || !data || !data.wallet_key_password_hash) {
            return false;
        }

        return verifyWalletPassword(password, data.wallet_key_password_hash, APP_SALT);
    } catch (error) {
        console.error('Error verifying wallet key password:', error);
        return false;
    }
}

// Change wallet key password
export async function changeWalletKeyPassword(walletAddress: string, currentPassword: string, newPassword: string): Promise<boolean> {
    try {
        // First verify the current password
        const isValid = await verifyWalletKeyPassword(walletAddress, currentPassword);

        if (!isValid) {
            return false;
        }

        // Hash the new password
        const newPasswordHash = hashWalletPassword(newPassword, APP_SALT);

        // Update the password hash in the database
        const { error } = await adminSupabase
            .from('users')
            .update({ wallet_key_password_hash: newPasswordHash })
            .eq('wallet_address', walletAddress);

        if (error) {
            console.error('Error changing wallet key password:', error);
            return false;
        }

        return true;
    } catch (error) {
        console.error('Error changing wallet key password:', error);
        return false;
    }
}

// Initiate password reset (requires user to have email set)
export async function initiateWalletPasswordReset(walletAddress: string, email: string): Promise<boolean> {
    try {
        // Verify the email matches the user
        const { data: userData, error: userError } = await adminSupabase
            .from('users')
            .select('email')
            .eq('wallet_address', walletAddress)
            .single();

        if (userError || !userData || userData.email !== email) {
            return false;
        }

        // Generate a reset token
        const resetToken = generateResetToken();
        const expiryTime = new Date();
        expiryTime.setHours(expiryTime.getHours() + 1); // Token valid for 1 hour

        // Store the reset token
        const { error } = await adminSupabase
            .from('users')
            .update({
                wallet_password_reset_token: resetToken,
                wallet_password_reset_expires: expiryTime.toISOString()
            })
            .eq('wallet_address', walletAddress);

        if (error) {
            console.error('Error generating reset token:', error);
            return false;
        }

        // Here you would send an email with the reset token/link
        // This is implementation-specific and would need to be connected
        // to your email service

        return true;
    } catch (error) {
        console.error('Error initiating wallet password reset:', error);
        return false;
    }
}

// Reset password with token
export async function resetWalletPasswordWithToken(resetToken: string, newPassword: string): Promise<boolean> {
    try {
        // Find user with this token and verify it's not expired
        const now = new Date().toISOString();

        const { data, error } = await adminSupabase
            .from('users')
            .select('wallet_address, wallet_password_reset_expires')
            .eq('wallet_password_reset_token', resetToken)
            .gte('wallet_password_reset_expires', now)
            .single();

        if (error || !data) {
            console.error('Invalid or expired reset token');
            return false;
        }

        // Hash the new password
        const newPasswordHash = hashWalletPassword(newPassword, APP_SALT);

        // Update the password and clear the reset token
        const { error: updateError } = await adminSupabase
            .from('users')
            .update({
                wallet_key_password_hash: newPasswordHash,
                wallet_password_reset_token: null,
                wallet_password_reset_expires: null
            })
            .eq('wallet_address', data.wallet_address);

        if (updateError) {
            console.error('Error resetting password:', updateError);
            return false;
        }

        return true;
    } catch (error) {
        console.error('Error resetting wallet password:', error);
        return false;
    }
}

/**
 * Updates the active trading wallet for a user
 */
export const updateActiveTradingWallet = async (
    walletAddress: string,
    activeTradingWalletPublicKey: string | null
): Promise<boolean> => {
    try {
        // Get current active wallet first
        const { data } = await adminSupabase
            .from('users')
            .select('active_trade_wallet')
            .eq('wallet_address', walletAddress)
            .single();

        const previousActiveWallet = data?.active_trade_wallet || null;

        // Update user record with active trading wallet
        const { error } = await adminSupabase
            .from('users')
            .update({
                active_trade_wallet: activeTradingWalletPublicKey,
                updated_at: new Date().toISOString()
            })
            .eq('wallet_address', walletAddress);

        if (error) {
            console.error("Error updating active trading wallet:", error);
            return false;
        }

        // Notify components about active wallet change with correct previous wallet
        notifyActiveWalletChanged(previousActiveWallet, activeTradingWalletPublicKey);

        // Clear the profile cache for this user after updating
        profileCache.delete(walletAddress);

        return true;
    } catch (error) {
        console.error("Exception updating active trading wallet:", error);
        return false;
    }
};

/**
 * Ensures a user exists in the database - creates one if it doesn't exist
 * This is a safety function to ensure we always have a user record before logging trades
 */
export const ensureUserExists = async (walletAddress: string): Promise<string | null> => {
    try {
        console.log(`🔍 Ensuring user exists for wallet: ${walletAddress}`);

        // First check if user already exists
        const { data: existingUser, error: checkError } = await adminSupabase
            .from('users')
            .select('id')
            .eq('wallet_address', walletAddress)
            .single();

        if (existingUser && !checkError) {
            console.log(`✅ User already exists with ID: ${existingUser.id}`);
            return existingUser.id;
        }

        console.log(`👤 User doesn't exist, creating new user for wallet: ${walletAddress}`);

        // Create new user with minimal required data
        const newUserData = {
            wallet_address: walletAddress,
            wallet_provider: 'unknown', // Will be updated when wallet connects properly
            last_login: new Date().toISOString(),
            login_count: 1,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
        };

        const { data: newUser, error: createError } = await adminSupabase
            .from('users')
            .insert([newUserData])
            .select('id')
            .single();

        if (createError) {
            console.error('❌ Error creating user:', createError);
            return null;
        }

        if (!newUser) {
            console.error('❌ No user data returned after creation');
            return null;
        }

        console.log(`✅ Created new user with ID: ${newUser.id}`);

        // Clear cache to ensure fresh data
        profileCache.delete(walletAddress);

        return newUser.id;
    } catch (error) {
        console.error('❌ Exception ensuring user exists:', error);
        return null;
    }
};
